import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { colorPalette } from "../styles/theme";
import { useCallback, useEffect, useState } from "react";
import { wsApi } from "../api/services";
import InquiryDetailView from "./InquiryDetailView";

const PAGE_SIZE = 10;
const DISPLAY_PAGE_COUNT = 5;

const inquiryColumns = [
  { key: "id", label: "구분", align: "center" },
  { key: "category", label: "문의유형", align: "left" },
  { key: "title", label: "제목", align: "left" },
  { key: "created_at", label: "등록일", align: "center" },
  { key: "status", label: "답변현황", align: "center" },
];

const statusDict = {
  PENDING: "접수완료",
  PROGRESS: "확인중",
  COMPLETE: "답변완료",
};

const statusSx = {
  PENDING: {
    color: colorPalette.textSecondary,
    backgroundColor: colorPalette.secondary,
    borderColor: colorPalette.border,
  },
  PROGRESS: {
    color: "#0f9f4f",
    backgroundColor: "#e7f7ee",
    borderColor: "#bfe8cf",
  },
  COMPLETE: {
    color: colorPalette.primary,
    backgroundColor: colorPalette.activeBg,
    borderColor: "#c8d9fb",
  },
};

const InquiryView = () => {
  const [inquiryStatus, setInquiryStatus] = useState("전체");
  const [inquiryList, setInquiryList] = useState([]);
  const [inquiryCount, setInquiryCount] = useState(0);
  const [inquiryDetail, setInquiryDetail] = useState(null);
  const [selectInquiry, setSelectInquiry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCnt, setPageCnt] = useState(1);

  const pageGroup = Math.ceil(currentPage / DISPLAY_PAGE_COUNT);
  const startPage = (pageGroup - 1) * DISPLAY_PAGE_COUNT + 1;
  const endPage = Math.min(pageGroup * DISPLAY_PAGE_COUNT, pageCnt);
  const hasPrevGroup = startPage !== 1;
  const hasNextGroup = endPage !== pageCnt;

  const getInquiryList = useCallback(() => {
    wsApi
      .getInquiryList(inquiryStatus, PAGE_SIZE, currentPage)
      .then((res) => {
        const totalCount = res.data.total_count ?? 0;

        setInquiryList(res.data.inquiries ?? []);
        setInquiryCount(totalCount);
        setPageCnt(Math.max(1, Math.ceil(totalCount / PAGE_SIZE)));
      })
      .catch((err) => {
        // console.error("[InquiryView] 문의 목록 로드 실패:", err);
      });
  }, [currentPage, inquiryStatus]);

  useEffect(() => {
    getInquiryList();
  }, [getInquiryList]);

  const handleStatusChange = (event) => {
    setInquiryStatus(event.target.value);
    setCurrentPage(1);
  };

  const handleSelectInquiry = (inquiry) => {
    setInquiryDetail(inquiry);
    setSelectInquiry(true);
  };

  const getCellValue = (inquiry, key) => {
    if (key === "created_at") return inquiry.created_at?.slice(0, 10) ?? "-";
    if (key === "status") return statusDict[inquiry.status] ?? inquiry.status;
    return inquiry[key] ?? "-";
  };

  if (selectInquiry) {
    return (
      <InquiryDetailView
        inquiry={inquiryDetail}
        selectInquiry={selectInquiry}
        setSelectInquiry={setSelectInquiry}
        getInquiryList={getInquiryList}
      />
    );
  }

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: colorPalette.white,
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${colorPalette.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: colorPalette.textPrimary }}
          >
            문의 내역
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: colorPalette.textSecondary, fontSize: 13 }}
          >
            총 {inquiryCount}개
          </Typography>
        </Box>

        <Select
          size="small"
          value={inquiryStatus}
          onChange={handleStatusChange}
          sx={{
            width: 150,
            fontSize: 13,
            backgroundColor: colorPalette.white,
            "& .MuiSelect-select": { py: 1.1 },
          }}
        >
          <MenuItem value="전체" sx={{ fontSize: 14 }}>
            전체
          </MenuItem>
          <MenuItem value="접수됨만" sx={{ fontSize: 14 }}>
            접수됨
          </MenuItem>
          <MenuItem value="답변완료 제외" sx={{ fontSize: 14 }}>
            답변완료 제외
          </MenuItem>
        </Select>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "80px 140px minmax(220px, 1fr) 130px 120px",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderRadius: 1,
            backgroundColor: colorPalette.background,
            border: `1px solid ${colorPalette.border}`,
          }}
        >
          {inquiryColumns.map((col) => (
            <Typography
              key={col.key}
              variant="caption"
              sx={{
                color: colorPalette.textSecondary,
                fontWeight: 700,
                textAlign: col.align,
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {inquiryList.length === 0 ? (
            <Box
              sx={{
                py: 8,
                textAlign: "center",
                border: `1px dashed ${colorPalette.border}`,
                borderRadius: 1,
                color: colorPalette.textSecondary,
              }}
            >
              <Typography variant="body2">문의 내역이 없습니다.</Typography>
            </Box>
          ) : (
            inquiryList.map((inquiry) => (
              <Box
                key={inquiry.id}
                role="button"
                tabIndex={0}
                onClick={() => handleSelectInquiry(inquiry)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleSelectInquiry(inquiry);
                  }
                }}
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "80px 140px minmax(220px, 1fr) 130px 120px",
                  alignItems: "center",
                  px: 2,
                  py: 1.8,
                  border: `1px solid ${colorPalette.border}`,
                  borderRadius: 1,
                  backgroundColor: colorPalette.white,
                  cursor: "pointer",
                  transition:
                    "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
                  "&:hover": {
                    borderColor: "#c8d9fb",
                    backgroundColor: "#f8fbff",
                    boxShadow: "0 2px 8px rgba(45, 108, 223, 0.08)",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${colorPalette.primary}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {inquiryColumns.map((col) => {
                  const value = getCellValue(inquiry, col.key);

                  if (col.key === "status") {
                    return (
                      <Box
                        key={col.key}
                        sx={{ display: "flex", justifyContent: "center" }}
                      >
                        <Chip
                          label={value}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 24,
                            fontSize: 12,
                            fontWeight: 700,
                            ...(statusSx[inquiry.status] ?? statusSx.PENDING),
                          }}
                        />
                      </Box>
                    );
                  }

                  return (
                    <Typography
                      key={col.key}
                      variant="body2"
                      title={String(value)}
                      sx={{
                        color:
                          col.key === "title"
                            ? colorPalette.textPrimary
                            : colorPalette.textSecondary,
                        fontWeight: col.key === "title" ? 600 : 500,
                        fontSize: 13,
                        textAlign: col.align,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        pr: col.key === "title" ? 2 : 0,
                      }}
                    >
                      {value}
                    </Typography>
                  );
                })}
              </Box>
            ))
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            mt: 3,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {currentPage !== 1 && (
            <Button size="small" onClick={() => setCurrentPage(1)}>
              &lt;&lt;
            </Button>
          )}
          {hasPrevGroup && (
            <Button size="small" onClick={() => setCurrentPage(currentPage - 1)}>
              &lt;
            </Button>
          )}
          {Array.from({ length: endPage - startPage + 1 }, (_, index) => {
            const pageNumber = startPage + index;
            const isActive = currentPage === pageNumber;

            return (
              <Button
                key={pageNumber}
                size="small"
                variant={isActive ? "contained" : "text"}
                onClick={() => setCurrentPage(pageNumber)}
                sx={{
                  minWidth: 34,
                  color: isActive ? colorPalette.white : colorPalette.textSecondary,
                }}
              >
                {pageNumber}
              </Button>
            );
          })}
          {hasNextGroup && (
            <Button size="small" onClick={() => setCurrentPage(currentPage + 1)}>
              &gt;
            </Button>
          )}
          {currentPage !== pageCnt && (
            <Button size="small" onClick={() => setCurrentPage(pageCnt)}>
              &gt;&gt;
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default InquiryView;
